import React from 'react';
import { Calendar, momentLocalizer, Event } from 'react-big-calendar';
import moment from 'moment';
import 'moment/locale/de';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { useAppointments } from '../../../lib/hooks/useAppointments';
import { Appointment, AppointmentType } from '../../../lib/interfaces/appointment.interface';
import { Loader2 } from 'lucide-react';

// Set moment to German locale
moment.locale('de');

const localizer = momentLocalizer(moment);

interface DealCalendarProps {
  dealId: string;
}

const DealCalendar: React.FC<DealCalendarProps> = ({ dealId }) => {
  const { appointments, loading } = useAppointments({ dealId });

  const events: Event[] = appointments.map((appointment: Appointment) => ({
    id: appointment._id,
    title: appointment.title,
    start: new Date(appointment.start),
    end: new Date(appointment.end),
    resource: appointment,
  }));

  const customEventStyle = (event: Event) => {
    const appointment = event.resource as Appointment;
    let backgroundColor = '#3174ad';
    
    switch (appointment.type) {
      case AppointmentType.CALL:
        backgroundColor = '#10b981'; // green
        break;
      case AppointmentType.MEETING:
        backgroundColor = '#3b82f6'; // blue
        break;
      case AppointmentType.EMAIL:
        backgroundColor = '#8b5cf6'; // purple
        break;
      case AppointmentType.TASK:
        backgroundColor = '#f59e0b'; // amber
        break;
    }

    return {
      style: {
        backgroundColor,
        borderRadius: '4px',
        opacity: 0.9,
        color: 'white',
        border: '0px',
        display: 'block',
      },
    };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="h-[600px]">
      <Calendar
        localizer={localizer}
        events={events}
        startAccessor="start"
        endAccessor="end"
        style={{ height: '100%' }}
        views={['week', 'day', 'agenda']}
        defaultView='week'
        eventPropGetter={customEventStyle}
        min={new Date(0, 0, 0, 7, 0, 0)}
        max={new Date(0, 0, 0, 19, 0, 0)}
        step={30}
        timeslots={2}
        messages={{
          today: 'Heute',
          previous: 'Zurück',
          next: 'Weiter',
          month: 'Monat',
          week: 'Woche',
          day: 'Tag',
          agenda: 'Agenda',
          date: 'Datum',
          time: 'Zeit',
          event: 'Termin',
          noEventsInRange: 'Keine Termine in diesem Zeitraum.',
          allDay: 'Ganztägig',
        }}
      />
    </div>
  );
};

export default DealCalendar;
